/**
 * Licensing API service for Xero integration, license criteria, and dual-gate activation
 */

import { apiClient, tokenStorage } from './api'
import type {
  XeroInvoiceState,
  ManualXeroInvoiceRequest,
  LinkToEntitlementRequest,
  ImportResponse,
  PaginatedXeroInvoiceStatesResponse,
  ListXeroInvoiceStatesParams,
  Entitlement,
  PaginatedEntitlementsResponse,
  ListEntitlementsParams,
  LicenseCriteria,
  ManualLicenseCriteriaRequest,
  PaginatedLicenseCriteriaResponse,
  ListLicenseCriteriaParams,
  ActivationStatus,
  ActivationResult,
  CreateEntitlementFromCriteriaRequest,
  LicenseExpirationReportResponse,
  LicenseExpirationQueryParams,
} from '../types'

import { API_BASE_URL } from './api-base-url'

// =============================================================================
// Xero Invoice State API
// =============================================================================

/**
 * List Xero invoice states with optional filters
 */
export async function listXeroInvoiceStates(
  params?: ListXeroInvoiceStatesParams
): Promise<PaginatedXeroInvoiceStatesResponse> {
  const searchParams = new URLSearchParams()
  if (params?.limit) searchParams.set('limit', params.limit.toString())
  if (params?.offset) searchParams.set('offset', params.offset.toString())
  if (params?.status) searchParams.set('status', params.status)
  if (params?.xeroTenantId) searchParams.set('xeroTenantId', params.xeroTenantId)

  const query = searchParams.toString()
  const endpoint = `/admin/import/xero${query ? `?${query}` : ''}`
  return apiClient.get<PaginatedXeroInvoiceStatesResponse>(endpoint)
}

/**
 * Get a Xero invoice state by ID
 */
export async function getXeroInvoiceState(id: string): Promise<XeroInvoiceState> {
  return apiClient.get<XeroInvoiceState>(`/admin/import/xero/${id}`)
}

/**
 * Get Xero invoice state by entitlement ID
 */
export async function getXeroInvoiceStateByEntitlement(
  entitlementId: string
): Promise<XeroInvoiceState | null> {
  return apiClient.get<XeroInvoiceState | null>(
    `/admin/import/xero/by-entitlement/${entitlementId}`
  )
}

/**
 * Import Xero invoices from JSON file
 */
export async function importXeroInvoicesFromJson(file: File): Promise<ImportResponse> {
  const formData = new FormData()
  formData.append('file', file)

  const token = tokenStorage.getAccessToken()
  const response = await fetch(`${API_BASE_URL}/admin/import/xero/json`, {
    method: 'POST',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
    body: formData,
  })

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => null)) as { message?: string } | null
    throw new Error(errorBody?.message ?? response.statusText)
  }

  return response.json() as Promise<ImportResponse>
}

/**
 * Create/update Xero invoice state from manual form
 */
export async function createXeroInvoiceFromForm(
  request: ManualXeroInvoiceRequest
): Promise<ImportResponse> {
  return apiClient.post<ImportResponse>('/admin/import/xero/form', request)
}

/**
 * Link a Xero invoice state to an entitlement
 */
export async function linkXeroToEntitlement(
  xeroInvoiceStateId: string,
  request: LinkToEntitlementRequest
): Promise<XeroInvoiceState> {
  return apiClient.post<XeroInvoiceState>(`/admin/import/xero/${xeroInvoiceStateId}/link`, request)
}

// =============================================================================
// Entitlement API
// =============================================================================

/**
 * List entitlements with optional filters
 */
export async function listEntitlements(
  params?: ListEntitlementsParams
): Promise<PaginatedEntitlementsResponse> {
  const searchParams = new URLSearchParams()
  if (params?.limit) searchParams.set('limit', params.limit.toString())
  if (params?.offset) searchParams.set('offset', params.offset.toString())
  if (params?.organisationId) searchParams.set('organisationId', params.organisationId)
  if (params?.status) searchParams.set('status', params.status)

  const query = searchParams.toString()
  const endpoint = `/admin/entitlements${query ? `?${query}` : ''}`
  return apiClient.get<PaginatedEntitlementsResponse>(endpoint)
}

/**
 * Get an entitlement by ID
 */
export async function getEntitlement(id: string): Promise<Entitlement> {
  return apiClient.get<Entitlement>(`/admin/entitlements/${id}`)
}

/**
 * Pass payment gate on an entitlement
 */
export async function passPaymentGate(entitlementId: string): Promise<Entitlement> {
  return apiClient.post<Entitlement>(`/admin/entitlements/${entitlementId}/pass-payment-gate`)
}

/**
 * Pass license gate on an entitlement
 */
export async function passLicenseGate(entitlementId: string): Promise<Entitlement> {
  return apiClient.post<Entitlement>(`/admin/entitlements/${entitlementId}/pass-license-gate`)
}

/**
 * Activate an entitlement (requires both gates to pass)
 */
export async function activateEntitlement(entitlementId: string): Promise<Entitlement> {
  return apiClient.post<Entitlement>(`/admin/entitlements/${entitlementId}/activate`)
}

/**
 * Suspend an entitlement
 */
export async function suspendEntitlement(entitlementId: string): Promise<Entitlement> {
  return apiClient.post<Entitlement>(`/admin/entitlements/${entitlementId}/suspend`)
}

/**
 * Reactivate a suspended entitlement
 */
export async function reactivateEntitlement(entitlementId: string): Promise<Entitlement> {
  return apiClient.post<Entitlement>(`/admin/entitlements/${entitlementId}/reactivate`)
}

/**
 * Revoke an entitlement
 */
export async function revokeEntitlement(entitlementId: string): Promise<Entitlement> {
  return apiClient.post<Entitlement>(`/admin/entitlements/${entitlementId}/revoke`)
}

// =============================================================================
// License Criteria API
// =============================================================================

/**
 * List license criteria with optional filters
 */
export async function listLicenseCriteria(
  params?: ListLicenseCriteriaParams
): Promise<PaginatedLicenseCriteriaResponse> {
  const searchParams = new URLSearchParams()
  if (params?.limit) searchParams.set('limit', params.limit.toString())
  if (params?.offset) searchParams.set('offset', params.offset.toString())
  if (params?.status) searchParams.set('status', params.status)
  if (params?.organisationId) searchParams.set('organisationId', params.organisationId)

  const query = searchParams.toString()
  const endpoint = `/admin/import/criteria${query ? `?${query}` : ''}`
  return apiClient.get<PaginatedLicenseCriteriaResponse>(endpoint)
}

/**
 * Get license criteria by ID
 */
export async function getLicenseCriteria(id: string): Promise<LicenseCriteria> {
  return apiClient.get<LicenseCriteria>(`/admin/import/criteria/${id}`)
}

/**
 * Get available (verified) criteria for an organisation
 */
export async function getAvailableCriteria(organisationId: string): Promise<LicenseCriteria[]> {
  return apiClient.get<LicenseCriteria[]>(`/admin/import/criteria/available/${organisationId}`)
}

/**
 * Import license criteria from JSON file
 */
export async function importLicenseCriteriaFromJson(file: File): Promise<ImportResponse> {
  const formData = new FormData()
  formData.append('file', file)

  const token = tokenStorage.getAccessToken()
  const response = await fetch(`${API_BASE_URL}/admin/import/criteria/json`, {
    method: 'POST',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
    body: formData,
  })

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => null)) as { message?: string } | null
    throw new Error(errorBody?.message ?? response.statusText)
  }

  return response.json() as Promise<ImportResponse>
}

/**
 * Create license criteria from manual form
 */
export async function createLicenseCriteriaFromForm(
  request: ManualLicenseCriteriaRequest
): Promise<LicenseCriteria> {
  return apiClient.post<LicenseCriteria>('/admin/import/criteria/form', request)
}

/**
 * Verify license criteria
 */
export async function verifyCriteria(id: string, notes?: string): Promise<LicenseCriteria> {
  return apiClient.post<LicenseCriteria>(`/admin/import/criteria/${id}/verify`, { notes })
}

/**
 * Mark license criteria as invalid
 */
export async function invalidateCriteria(id: string): Promise<LicenseCriteria> {
  return apiClient.post<LicenseCriteria>(`/admin/import/criteria/${id}/invalidate`)
}

/**
 * Link license criteria to an invoice
 *
 * This allows orphaned criteria (created without an invoice link) to be
 * linked to a paid invoice, satisfying the payment gate requirement.
 */
export async function linkCriteriaToInvoice(
  criteriaId: string,
  xeroInvoiceStateId: string
): Promise<LicenseCriteria> {
  return apiClient.put<LicenseCriteria>(`/admin/import/criteria/${criteriaId}/link-invoice`, {
    xeroInvoiceStateId,
  })
}

/**
 * Unlink license criteria from an invoice
 */
export async function unlinkCriteriaFromInvoice(criteriaId: string): Promise<LicenseCriteria> {
  return apiClient.delete<LicenseCriteria>(`/admin/import/criteria/${criteriaId}/link-invoice`)
}

// =============================================================================
// Activation Workflow API
// =============================================================================

/**
 * Get activation status for an entitlement
 */
export async function getActivationStatus(entitlementId: string): Promise<ActivationStatus> {
  return apiClient.get<ActivationStatus>(`/admin/activation/${entitlementId}/status`)
}

/**
 * Create entitlement from verified license criteria
 */
export async function createEntitlementFromCriteria(
  request: CreateEntitlementFromCriteriaRequest
): Promise<Entitlement> {
  return apiClient.post<Entitlement>('/admin/activation/create-entitlement', request)
}

/**
 * Link Xero invoice to entitlement (auto-checks payment gate)
 */
export async function linkInvoiceToEntitlement(
  entitlementId: string,
  invoiceId: string
): Promise<Entitlement> {
  return apiClient.post<Entitlement>(`/admin/activation/${entitlementId}/link-invoice/${invoiceId}`)
}

/**
 * Link license criteria to entitlement (auto-checks license gate)
 */
export async function linkCriteriaToEntitlement(
  entitlementId: string,
  criteriaId: string
): Promise<Entitlement> {
  return apiClient.post<Entitlement>(
    `/admin/activation/${entitlementId}/link-criteria/${criteriaId}`
  )
}

/**
 * Activate entitlement and create license
 */
export async function activateAndCreateLicense(
  entitlementId: string,
  notes?: string
): Promise<ActivationResult> {
  return apiClient.post<ActivationResult>(`/admin/activation/${entitlementId}/activate`, { notes })
}

// =============================================================================
// Reports API
// =============================================================================

/**
 * Get license expiration report
 */
export async function getLicenseExpirationReport(
  params?: LicenseExpirationQueryParams
): Promise<LicenseExpirationReportResponse> {
  const searchParams = new URLSearchParams()
  if (params?.organisationId) searchParams.set('organisationId', params.organisationId)
  if (params?.expiresWithinDays !== undefined) {
    searchParams.set('expiresWithinDays', params.expiresWithinDays.toString())
  }
  if (params?.includeExpired) searchParams.set('includeExpired', 'true')

  const query = searchParams.toString()
  const endpoint = `/admin/reports/license-expiration${query ? `?${query}` : ''}`
  return apiClient.get<LicenseExpirationReportResponse>(endpoint)
}
